<template>
  <div class="order-item" :id="`order-item-${item.id}`" :class="{
    'order-item-printable': isPrintable
  }">
    <!-- Single Header Row with everything -->
    <div class="order-item-header">
      <div class="order-item-info">
        <!-- Product name -->
        <div class="order-item-name">
          <strong>{{ displayTitle }}</strong>
        </div>
      </div>

      <div class="order-item-details">
        <!-- Print format badge -->
        <CBadge v-if="isPrintable && printFormat" color="info" class="mr-2">
          {{ printFormat }}
        </CBadge>

        <!-- Finish badge -->
        <CBadge v-if="isPrintable && printConfig && printConfig.finish" color="secondary" class="mr-2">
          {{ printConfig.finish }}
        </CBadge>

        <!-- Quantity badge - only for printable items -->
        <CBadge
          v-if="isPrintable"
          :color="item.quantity > 1 ? 'warning' : 'light'"
          class="mr-2"
        >
          <CIcon v-if="item.quantity > 1" name="cilWarning" size="sm" class="mr-1"/>
          <translate translate-context="edge-orders.item">Qty</translate>
          <span class="ml-1 font-weight-bold">{{ item.quantity }}</span>
        </CBadge>

        <!-- Total prints badge if quantity > 1 (only for printable) -->
        <CBadge v-if="shouldShowTotalPrintsBadge" color="warning" class="mr-2">
          <CIcon name="cilPrint" size="sm" class="mr-1"/>
          <translate translate-context="edge-orders.item"
                    :translate-params="{total: totalPrints}">
            %{total} total prints
          </translate>
        </CBadge>

        <!-- Total price (only for non-bundle items) -->
        <div v-if="hasPerm('edge.orders.total') && !isPartOfBundle" class="order-item-price">
          <strong>{{ calculateFullPrice | currency(order.currency) }}</strong>
        </div>
      </div>
    </div>

    <!-- Media Preview Gallery -->
    <OrderMediaPreview
      v-if="hasMedia && hasPerm('edge.orders.media.list')"
      :order-item="item"
      :order="order"
      :media-getter="getMediaForOrderItem"
      :selected-media-getter="getSelectedMediaForOrderItem"
      :loading-getter="isMediaLoadingForOrderItem"
      :fetch-media-action="fetchMediaAction"
      :toggle-selection-action="toggleSelectionAction"
      :context-items="contextMenuItems"
      :allow-selection="false"
      @bulk-select="handleBulkSelect"
      @context-action="handleContextAction"
      @zoom="$emit('zoom', $event)"
      class="mt-2"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OrderMediaPreview from '../shared/OrderMediaPreview.vue'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'OrderItem',
  components: {
    OrderMediaPreview
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapGetters('edgeOrders', [
      'getSelectedMediaForOrderItem',
      'getMediaForOrderItem',
      'isMediaLoadingForOrderItem'
    ]),

    isPartOfBundle() {
      return !!(this.item.bundle && this.item.bundle.id)
    },

    productName() {
      if (this.item.bundle) {
        return this.item.bundle.name
      } else if (this.item.product) {
        return this.item.product.name
      }
      return '-'
    },

    displayTitle() {
      // Combine product name with variant name if they're different
      const baseName = this.productName
      const variantName = this.item.product_variant?.name

      if (!variantName || variantName === baseName) {
        return baseName
      }

      // If variant name contains product name, just use variant name
      if (variantName.includes(baseName)) {
        return variantName
      }

      // Otherwise combine them
      return `${baseName} - ${variantName}`
    },

    hasMedia() {
      return (this.item.media_ids && this.item.media_ids.length > 0) ||
             (this.item.gallery_ids && this.item.gallery_ids.length > 0) ||
             (this.item.product?.gallery_ids && this.item.product.gallery_ids.length > 0)
    },

    mediaCounts() {
      const mediaData = this.getMediaForOrderItem(this.item.id)
      return { total: mediaData.media?.length || 0 }
    },

    selectedMediaCount() {
      const selected = this.getSelectedMediaForOrderItem(this.item.id)
      return selected ? selected.length : 0
    },

    isPrintable() {
      return this.item.is_printable === true
    },

    printConfig() {
      return this.item.product_variant?.config || this.item.product?.config || null
    },

    printFormat() {
      if (!this.printConfig?.format_cm) return ''
      return this.printConfig.format_cm
    },

    totalPrints() {
      // Total number of prints needed (photos × quantity)
      return this.mediaCounts.total * this.item.quantity
    },

    shouldShowTotalPrintsBadge() {
      return this.isPrintable && this.item.quantity > 1 && this.mediaCounts.total > 0
    },

    calculateFullPrice() {
      return parseFloat(this.item.unit_price) * this.item.quantity
    },

    contextMenuItems() {
      const items = []

      // Download action only for digital (non-printable) items with selections and permission
      if (!this.isPrintable && this.selectedMediaCount > 0 && this.hasPerm('edge.orders.media.download')) {
        items.push({
          value: 'download',
          label: this.$pgettext('edge-orders.item', 'Download'),
          icon: 'cilCloudDownload'
        })
      }

      return items
    }
  },
  methods: {
    ...mapActions('edgeOrders', [
      'selectAllMediaForOrderItem',
      'deselectAllMediaForOrderItem',
      'fetchMediaForOrderItemId',
      'toggleMediaSelectionForOrderItem',
      'downloadMedia'
    ]),

    selectAll() {
      this.selectAllMediaForOrderItem(this.item.id)
    },

    deselectAll() {
      this.deselectAllMediaForOrderItem(this.item.id)
    },

    fetchMediaAction(params) {
      return this.fetchMediaForOrderItemId(params)
    },

    toggleSelectionAction(params) {
      return this.toggleMediaSelectionForOrderItem(params)
    },

    handleBulkSelect({ orderItemId, selectedIds }) {
      this.$store.commit('edgeOrders/setOrderItemMediaSelection', {
        orderItemId,
        selectedIds
      })
    },

    async downloadSelectedForItem() {
      const selectedMediaIds = this.getSelectedMediaForOrderItem(this.item.id)

      if (selectedMediaIds.length === 0) {
        return
      }

      const picaCode = this.order.picacode
      if (!picaCode) {
        await this.notifyError(this.$pgettext('edge-orders.item', 'No picacode found for this order'))
        return
      }

      try {
        await this.downloadMedia({
          mediaIds: selectedMediaIds,
          picaCode: picaCode
        })

        await this.notifySuccess(
          this.$pgettext('edge-orders.item', 'Download started for selected photos')
        )
      } catch (error) {
        await this.notifyError(
          this.$pgettext('edge-orders.item', 'Failed to download photos')
        )
      }
    },

    handleContextAction(action) {
      switch (action) {
        case 'download':
          this.downloadSelectedForItem()
          break
      }
    }
  },
  filters: {
    currency: Filters.currency
  }
}
</script>

<style scoped>
.order-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: white;
  transition: all 0.2s;
}

.order-item-printable {
  border: 2px solid #007bff;
}

.order-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.order-item-printable:hover {
  box-shadow: 0 2px 12px rgba(0,123,255,0.15);
}

.order-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.order-item-info {
  flex: 1;
}

.order-item-name {
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.order-item-info {
  display: flex;
  align-items: center;
}

.order-item-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.order-item-price {
  font-size: 1.1rem;
  color: #2c3e50;
}

.order-item-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

.order-item-photo-info {
  display: flex;
  align-items: center;
}

.order-item-selection-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .order-item-header {
    flex-direction: column;
  }

  .order-item-details {
    margin-top: 0.75rem;
  }
}
</style>